<template>
    <div
        class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4 sm:px-6 sm:py-5"
    >
        <!-- Mobile: stacked rows. Desktop (lg+): 4-column grid with vertical dividers. -->
        <div
            class="flex flex-col lg:grid lg:grid-cols-4 lg:gap-6 lg:divide-x lg:divide-y-0 divide-y divide-slate-200 dark:divide-slate-700 lg:divide-slate-200 lg:dark:divide-slate-700"
        >
            <div
                class="flex items-center justify-between py-3 lg:flex-col lg:items-start lg:justify-start lg:py-0 lg:pl-0"
            >
                <dt
                    class="flex items-center gap-1 text-sm font-medium text-slate-500 dark:text-slate-400 lg:mb-1"
                >
                    Total value up to
                    <Tooltip
                        text="Total benefits available to use in selected period"
                        x-placement="center"
                        y-placement="top"
                        wrap
                    >
                        <template #trigger>
                            <Icon
                                icon="circle-info"
                                class="w-3.5 h-3.5 text-slate-400 cursor-help"
                            />
                        </template>
                    </Tooltip>
                </dt>
                <dd class="text-base lg:text-xl font-semibold text-slate-800 dark:text-slate-100">
                    <template v-if="locked">—</template>
                    <template v-else>{{ formatMoney(summary.total_value) }}</template>
                </dd>
            </div>

            <div
                class="flex items-center justify-between py-3 lg:flex-col lg:items-start lg:justify-start lg:py-0 lg:pl-6"
            >
                <dt
                    class="flex items-center gap-1 text-sm font-medium text-slate-500 dark:text-slate-400 lg:mb-1"
                >
                    Used
                    <Tooltip
                        text="Benefits used in selected period"
                        x-placement="center"
                        y-placement="top"
                        wrap
                    >
                        <template #trigger>
                            <Icon
                                icon="circle-info"
                                class="w-3.5 h-3.5 text-slate-400 cursor-help"
                            />
                        </template>
                    </Tooltip>
                </dt>
                <dd class="text-base lg:text-xl font-semibold text-slate-800 dark:text-slate-100">
                    <template v-if="locked">—</template>
                    <template v-else>{{ formatMoney(summary.used) }}</template>
                </dd>
            </div>

            <div
                class="flex items-center justify-between py-3 lg:flex-col lg:items-start lg:justify-start lg:py-0 lg:pl-6"
            >
                <dt
                    class="flex items-center gap-1 text-sm font-medium text-slate-500 dark:text-slate-400 lg:mb-1"
                >
                    Unused
                    <Tooltip
                        text="Benefits you did not use during the selected period"
                        x-placement="center"
                        y-placement="top"
                        wrap
                    >
                        <template #trigger>
                            <Icon
                                icon="circle-info"
                                class="w-3.5 h-3.5 text-slate-400 cursor-help"
                            />
                        </template>
                    </Tooltip>
                </dt>
                <dd class="text-base lg:text-xl font-semibold text-slate-800 dark:text-slate-100">
                    <template v-if="locked">—</template>
                    <template v-else>{{ formatMoney(summary.unused) }}</template>
                </dd>
            </div>

            <div
                class="flex items-center justify-between py-3 lg:flex-col lg:items-start lg:justify-start lg:py-0 lg:pl-6 relative"
            >
                <dt
                    class="flex items-center gap-1 text-sm font-medium text-slate-500 dark:text-slate-400 lg:mb-1"
                >
                    Utilization
                    <Tooltip
                        text="How much of total value you've used in selected period"
                        x-placement="center"
                        y-placement="top"
                        wrap
                    >
                        <template #trigger>
                            <Icon
                                icon="circle-info"
                                class="w-3.5 h-3.5 text-slate-400 cursor-help"
                            />
                        </template>
                    </Tooltip>
                </dt>
                <div class="flex items-center gap-2 lg:w-full lg:justify-between">
                    <div v-if="!locked" class="w-16 h-10 lg:order-2">
                        <svg class="w-16 h-10" viewBox="0 0 48 24">
                            <path
                                d="M 6 20 A 18 18 0 0 1 42 20"
                                fill="none"
                                stroke="#FED1C7"
                                stroke-width="6"
                            />
                            <path
                                d="M 6 20 A 18 18 0 0 1 42 20"
                                fill="none"
                                stroke="#FD421D"
                                stroke-width="6"
                                :stroke-dasharray="`${(summary.utilization / 100) * 56.5} 56.5`"
                                class="transition-all duration-300"
                            />
                        </svg>
                    </div>
                    <dd
                        class="text-base lg:text-xl font-semibold text-slate-800 dark:text-slate-100 lg:order-1"
                    >
                        <template v-if="locked">—</template>
                        <template v-else>{{ summary.utilization }}%</template>
                    </dd>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useCardBenefitsStore } from '@/store/card-benefits';
import Tooltip from '@/components/tooltip.vue';
import Icon from '@/components/ui/icon/icon.vue';

const store = useCardBenefitsStore();
const { insightsSummary, isSubscribed } = storeToRefs(store);
const summary = computed(() => insightsSummary.value);
// Free users see the dashboard scaffold but the historical aggregates are
// gated behind 10xTravel+. Render placeholder dashes instead of values.
const locked = computed(() => !isSubscribed.value);

const formatMoney = (value) => {
    if (!value && value !== 0) return '$0';
    const numValue = Math.round(Number(value));
    return '$' + numValue.toLocaleString('en-US');
};
</script>
